import { BODY, FLY, SLIDE, SWING, SIM_DT, clamp } from "./config";
import type { Rng } from "./rng";
import type { RopePathPoint } from "./ropeGeometry";

export type BenjiMode = "idle" | "hang" | "fly" | "fall" | "slide" | "celebrate";

/**
 * Minimal vine surface BenjiSim needs. Implemented by the client `Vine`
 * (PixiJS-backed) and by the headless vine state on the server — BenjiSim
 * itself stays free of any rendering / Pixi types.
 */
export interface VineRef {
  pivotWorldX: number;
  pivotWorldY: number;
  readonly length: number;
  active: boolean;
  pointAtDistance(distanceAlong: number): RopePathPoint;
  pinGrip(distanceAlong: number, worldX: number, worldY: number): void;
  releaseTip(): void;
}

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * Headless, deterministic Benji physics: pendulum hang, ballistic fly, vine
 * slide, post-bust fall, and the slide→hang seam easing. No sprites, no pose
 * alpha, no PixiJS — `bodyRotation` is the logical body angle the renderer
 * consumes. The only randomness (post-bust fall spin) goes through the
 * injected {@link Rng}. Advances strictly in `SIM_DT` steps.
 *
 * Extracted verbatim (behaviour-preserving) from the client `Benji`.
 */
export class BenjiSim {
  worldX = 0;
  worldY = 0;
  prevWorldX = 0;
  prevWorldY = 0;
  bodyRotation = 0;

  // Render-interpolation source: the transform at the START of the current
  // tick. The renderer lerps rPrev -> (worldX/Y/bodyRotation) by the leftover
  // accumulator fraction so motion is smooth at any display refresh rate.
  rPrevX = 0;
  rPrevY = 0;
  rPrevRot = 0;

  private mode: BenjiMode = "idle";
  private swingVine: VineRef | null = null;
  private hangLength = 0;
  private gripDistance = 0;

  private pendAngle = 0;
  private pendOmega = 0;
  private pumpSwing = false;

  private flyVX = 0;
  private flyVY = 0;

  private fallRotVel = 0;
  private freeRotation = 0;

  private slideDistance = 0;
  private slideOmega = 0;
  private slideSpeed = 0;
  private slideLastR = 1;

  private hangBlendX = 0;
  private hangBlendY = 0;
  private hangBlendRot = 0;
  private hangBlendT = 0;
  private hangBlendGripFrom = 0;
  // Slide→hang seam: ease the pendulum length from the real (slack) rope tip
  // radius up to the full vine length over the blend, so the handoff neither
  // yanks the vine (start matched) nor leaves it bent (ends straight).
  private seamLenFrom = 0;
  private rotationBlendFrom = 0;
  private rotationBlendT = 0;

  private celebrateTime = 0;

  constructor(private readonly rng: Rng) {}

  getMode(): BenjiMode {
    return this.mode;
  }
  getSwingVine(): VineRef | null {
    return this.swingVine;
  }
  setSwingPump(on: boolean) {
    this.pumpSwing = on;
  }

  resetState() {
    this.mode = "idle";
    this.detachVine();
    this.pumpSwing = false;
    this.pendAngle = 0;
    this.pendOmega = 0;
    this.hangLength = 0;
    this.gripDistance = 0;
    this.flyVX = 0;
    this.flyVY = 0;
    this.fallRotVel = 0;
    this.freeRotation = 0;
    this.slideDistance = 0;
    this.slideOmega = 0;
    this.slideSpeed = 0;
    this.slideLastR = 1;
    this.prevWorldX = 0;
    this.prevWorldY = 0;
    this.hangBlendX = 0;
    this.hangBlendY = 0;
    this.hangBlendRot = 0;
    this.hangBlendT = 0;
    this.hangBlendGripFrom = 0;
    this.rotationBlendFrom = 0;
    this.rotationBlendT = 0;
    this.celebrateTime = 0;
    this.bodyRotation = 0;
    this.snapRender();
  }

  startHang(
    vine: VineRef,
    initialAngle: number,
    initialOmega: number,
    length = vine.length,
    gripDistance = length,
  ) {
    const fromRotation = this.bodyRotation;
    this.attachVine(vine);
    this.hangLength = length;
    this.gripDistance = gripDistance;
    this.pendAngle = initialAngle;
    this.pendOmega = initialOmega;
    this.mode = "hang";
    this.syncHangPosition();
    this.rotationBlendFrom = wrapAngle(fromRotation - this.pendAngle);
    this.rotationBlendT = BODY.gripRotationBlend;
    vine.pinGrip(this.currentGripDistance(), this.worldX, this.worldY);
  }

  startFly(vx: number, vy: number) {
    this.detachVine();
    this.flyVX = vx;
    this.flyVY = vy;
    this.prevWorldX = this.worldX;
    this.prevWorldY = this.worldY;
    this.mode = "fly";
  }

  startFall(vx: number, vy: number, rotation: number) {
    this.detachVine();
    this.flyVX = vx;
    this.flyVY = vy;
    this.freeRotation = rotation;
    this.fallRotVel = (this.rng.next() > 0.5 ? 1 : -1) * (3 + this.rng.next() * 4);
    this.mode = "fall";
  }

  startSlide(vine: VineRef, distanceAlong: number, initialOmega: number) {
    this.attachVine(vine);
    this.slideDistance = Math.max(0, Math.min(distanceAlong, vine.length));
    this.gripDistance = this.slideDistance;
    this.slideOmega = initialOmega;
    this.slideSpeed = SLIDE.startSpeed;
    this.mode = "slide";
    this.syncSlidePosition();
    vine.pinGrip(this.gripDistance, this.worldX, this.worldY);
    const dx = this.worldX - vine.pivotWorldX;
    const dy = this.worldY - vine.pivotWorldY;
    this.slideLastR = Math.max(1, Math.hypot(dx, dy));
    this.snapRender(); // catch teleports here — don't interpolate the snap
  }

  startCelebrate() {
    this.celebrateTime = 0;
    this.mode = "celebrate";
  }

  getVelocity(): { vx: number; vy: number } {
    if (this.mode === "hang" && this.swingVine) {
      const L = this.hangLength || this.swingVine.length;
      return {
        vx: L * Math.cos(this.pendAngle) * this.pendOmega,
        vy: -L * Math.sin(this.pendAngle) * this.pendOmega,
      };
    }
    return { vx: this.flyVX, vy: this.flyVY };
  }

  /** Snap the render-interp source to the current pose (call after a
   * deliberate teleport so it isn't smeared across a frame). */
  private snapRender() {
    this.rPrevX = this.worldX;
    this.rPrevY = this.worldY;
    this.rPrevRot = this.bodyRotation;
  }

  /** Advance exactly one fixed `SIM_DT`. */
  step() {
    // Capture the pre-step pose for render interpolation.
    this.rPrevX = this.worldX;
    this.rPrevY = this.worldY;
    this.rPrevRot = this.bodyRotation;

    const dt = SIM_DT;
    switch (this.mode) {
      case "hang":
        this.updateHang(dt);
        break;
      case "fly":
        this.updateFly(dt);
        break;
      case "fall":
        this.updateFall(dt);
        break;
      case "slide":
        this.updateSlide(dt);
        break;
      case "celebrate":
        this.updateCelebrate(dt);
        break;
    }

    if (this.swingVine && (this.mode === "hang" || this.mode === "celebrate")) {
      this.swingVine.pinGrip(this.currentGripDistance(), this.worldX, this.worldY);
    }
  }

  private attachVine(vine: VineRef) {
    if (this.swingVine && this.swingVine !== vine) this.detachVine();
    this.swingVine = vine;
    vine.active = true;
  }

  private detachVine() {
    if (!this.swingVine) return;
    this.swingVine.active = false;
    this.swingVine.releaseTip();
    this.swingVine = null;
  }

  private updateHang(dt: number) {
    const v = this.swingVine;
    if (!v) return;
    const L = this.hangLength || v.length;
    const gEff = SWING.gravity / L;

    let accel = -gEff * Math.sin(this.pendAngle) - SWING.damping * this.pendOmega;

    if (this.pumpSwing) {
      const energy =
        0.5 * this.pendOmega * this.pendOmega + gEff * (1 - Math.cos(this.pendAngle));
      const targetEnergy = gEff * (1 - Math.cos(SWING.targetAngle));
      if (energy < targetEnergy) {
        const deficit = 1 - energy / targetEnergy;
        const dir = Math.tanh(this.pendOmega / SWING.pumpOmegaSoftness);
        accel += dir * SWING.pumpRate * deficit;
      }
    }

    this.pendOmega += accel * dt;
    this.pendAngle += this.pendOmega * dt;

    this.syncHangPosition();
    this.bodyRotation = this.hangBodyRotation(this.pendAngle);
    this.applyRotationBlend(dt);

    if (this.hangBlendT > 0) {
      this.hangBlendT = Math.max(0, this.hangBlendT - dt);
      const t = this.hangBlendT / BODY.slideToHangBlend; // 1 -> 0
      const k = t * t * (3 - 2 * t); // smoothstep
      this.worldX += this.hangBlendX * k;
      this.worldY += this.hangBlendY * k;
      this.bodyRotation += this.hangBlendRot * k;
    }
  }

  private syncHangPosition() {
    const v = this.swingVine;
    if (!v) return;
    let L = this.hangLength || v.length;
    // During the slide→hang seam, the effective length eases up from the real
    // slack-rope tip radius to L (smoothstep, 1→0 over the blend window).
    if (this.hangBlendT > 0) {
      const t = this.hangBlendT / BODY.slideToHangBlend;
      const k = t * t * (3 - 2 * t);
      L = L + (this.seamLenFrom - L) * k;
    }
    this.worldX = v.pivotWorldX + L * Math.sin(this.pendAngle);
    this.worldY = v.pivotWorldY + L * Math.cos(this.pendAngle);
  }

  private syncSlidePosition() {
    const v = this.swingVine;
    if (!v) return;
    const p = v.pointAtDistance(this.slideDistance);
    this.worldX = p.x;
    this.worldY = p.y;
    const trunk = Math.atan2(
      this.worldX - v.pivotWorldX,
      this.worldY - v.pivotWorldY,
    );
    this.bodyRotation = this.hangBodyRotation(trunk);
  }

  private updateSlide(dt: number) {
    const v = this.swingVine;
    if (!v) return;

    const dxr = this.worldX - v.pivotWorldX;
    const dyr = this.worldY - v.pivotWorldY;
    const r = Math.max(1, Math.hypot(dxr, dyr));
    const verticality = Math.max(0, dyr / r);
    this.slideSpeed = Math.min(
      SLIDE.maxSpeed,
      this.slideSpeed + SLIDE.gravity * verticality * dt,
    );
    this.slideDistance = Math.min(v.length, this.slideDistance + this.slideSpeed * dt);
    this.gripDistance = this.slideDistance;
    this.syncSlidePosition();
    v.pinGrip(this.gripDistance, this.worldX, this.worldY);

    {
      const dx = this.worldX - v.pivotWorldX;
      const dy = this.worldY - v.pivotWorldY;
      const rCur = Math.max(1, Math.hypot(dx, dy));
      const theta = Math.atan2(dx, dy);
      const ratio = Math.min(1, this.slideLastR / rCur);
      this.slideOmega *= ratio * ratio;
      this.slideOmega += -(SWING.gravity / rCur) * Math.sin(theta) * dt;
      this.slideOmega -= SWING.damping * this.slideOmega * dt;
      this.slideLastR = rCur;
    }

    if (this.slideDistance >= v.length - 0.5) {
      const fromX = this.worldX;
      const fromY = this.worldY;
      const fromRot = this.bodyRotation;
      const theta = Math.atan2(fromX - v.pivotWorldX, fromY - v.pivotWorldY);

      // Hang at the FULL vine length (final straight rope), but ease the
      // effective pendulum length up from the real slack-rope tip radius so
      // the first seam frame matches the rope exactly (no yank) and it
      // straightens out smoothly over the blend (no permanent bend).
      const seamR = Math.max(1, Math.hypot(fromX - v.pivotWorldX, fromY - v.pivotWorldY));
      this.startHang(v, theta, this.slideOmega);

      this.seamLenFrom = seamR;
      this.hangBlendX = 0; // position is handled by the length ease above
      this.hangBlendY = 0;
      this.hangBlendRot = wrapAngle(fromRot - this.bodyRotation);
      this.hangBlendT = BODY.slideToHangBlend;
      this.hangBlendGripFrom = this.slideDistance;
      this.bodyRotation = fromRot;
      this.syncHangPosition(); // re-place at the eased (== seamR) length now
    }
  }

  private updateFly(dt: number) {
    this.prevWorldX = this.worldX;
    this.prevWorldY = this.worldY;
    this.flyVY += FLY.gravity * dt;
    this.worldX += this.flyVX * dt;
    this.worldY += this.flyVY * dt;
    const pitch = Math.atan2(this.flyVY, this.flyVX);
    const target = clamp(
      pitch * BODY.flyTiltScale + BODY.flyForwardLean,
      -BODY.flyTiltLimit,
      BODY.flyTiltLimit,
    );
    const diff = wrapAngle(target - this.bodyRotation);
    this.bodyRotation += diff * Math.min(dt * BODY.flySmoothing, 1);
  }

  private updateFall(dt: number) {
    this.flyVY += FLY.gravity * dt;
    this.worldX += this.flyVX * dt;
    this.worldY += this.flyVY * dt;
    this.freeRotation += this.fallRotVel * dt;
    this.bodyRotation = this.freeRotation;
  }

  private updateCelebrate(dt: number) {
    this.celebrateTime += dt;
    const v = this.swingVine;
    if (!v) return;
    const wave = Math.sin(this.celebrateTime * 4.5) * 0.18;
    this.pendAngle = wave;
    this.syncHangPosition();
    this.worldY -= Math.abs(Math.sin(this.celebrateTime * 9)) * 5;
    this.bodyRotation = wave;
  }

  private hangBodyRotation(angle: number): number {
    return clamp(angle * BODY.hangTiltScale, -BODY.hangTiltLimit, BODY.hangTiltLimit);
  }

  private applyRotationBlend(dt: number) {
    if (this.rotationBlendT <= 0) return;
    this.rotationBlendT = Math.max(0, this.rotationBlendT - dt);
    const t = this.rotationBlendT / BODY.gripRotationBlend;
    const k = t * t * (3 - 2 * t);
    this.bodyRotation += this.rotationBlendFrom * k;
  }

  private currentGripDistance(): number {
    if (this.hangBlendT <= 0) return this.gripDistance;
    const t = this.hangBlendT / BODY.slideToHangBlend; // 1 -> 0
    const k = t * t * (3 - 2 * t);
    return this.gripDistance + (this.hangBlendGripFrom - this.gripDistance) * k;
  }
}
